import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Signup } from './pages/Signup';
import { Login } from './pages/Login';
import { Bell, DollarSign, Leaf, ArrowRight, CheckCircle2, Smartphone } from 'lucide-react';
import { Dashboard } from './pages/Dashboard';
import { AddItem } from './pages/AddItem';
import { EditItem } from './pages/EditItem';
import { Analytics } from './pages/Analytics';
import { AdvancedAnalytics } from './pages/AdvancedAnalytics';  // âœ… ADDED
import { HouseholdSettings } from './pages/HouseholdSettings';
import { CookBeforeExpires } from './pages/CookBeforeExpires';
import { AcceptInvite } from './pages/AcceptInvite';
import { JoinHousehold } from './pages/JoinHousehold';
import { PricingPage } from './pages/PricingPage';
import { TermsOfService } from './pages/TermsOfService';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import HowToUse from './pages/HowToUse';
import { EMOJIS } from './constants/emojis';

function HomePage() {
  const scrollToFeatures = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById('features');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="FridgeLife" className="w-10 h-10" />
              <h1 className="text-xl font-bold text-primary-green">FridgeLife</h1>
            </div>
            <div className="flex gap-2">
              <Link to="/login" className="px-4 py-2 text-sm text-primary-green font-medium hover:bg-primary-mint/50 rounded-lg transition-all">
                Log In
              </Link>
              <Link to="/signup" className="px-4 py-2 text-sm bg-primary-green text-white font-semibold rounded-lg hover:bg-primary-green/90 shadow-md transition-all whitespace-nowrap">
                Sign Up Free
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-6 px-4 py-2 bg-light-mint rounded-full">
              <span className="text-primary-green font-semibold text-sm">
                {EMOJIS.PLANT} Save Money {EMOJIS.BULLET} Reduce Waste {EMOJIS.BULLET} Live Better
              </span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Track your fridge.<br />
              <span className="text-primary-green">Save your money.</span>
            </h2>
            
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              FridgeLife helps you track your groceries, sends smart reminders, and saves you money by preventing food waste.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/signup" className="group px-8 py-4 bg-primary-green text-white font-semibold rounded-xl hover:bg-primary-green/90 shadow-xl shadow-primary-green/30 transition-all hover:shadow-2xl hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap">
                Get Started Free
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <button
                onClick={scrollToFeatures}
                className="px-8 py-4 bg-white text-primary-green font-semibold rounded-xl hover:bg-primary-mint/30 transition-all border-2 border-primary-green/20 whitespace-nowrap"
              >
                See How It Works
              </button>
            </div>
            <p className="text-sm text-gray-500">{EMOJIS.SPARKLES} Free for 30 days</p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 mb-16 border border-gray-100">
            <div className="aspect-video bg-gradient-to-br from-primary-mint to-primary-green/20 rounded-xl flex items-center justify-center">
              <div className="text-center">
                <img src="/logo.png" alt="FridgeLife Demo" className="w-32 h-32 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Your smart fridge assistant</p>
              </div>
            </div>
          </div>

          <div id="features" className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100">
              <div className="w-14 h-14 bg-light-mint rounded-xl flex items-center justify-center mb-4">
                <Bell className="w-7 h-7 text-primary-green" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Smart Reminders</h3>
              <p className="text-gray-600 leading-relaxed">
                Get notified 2 days before your food expires. Never forget about that yogurt in the back!
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100">
              <div className="w-14 h-14 bg-caution/20 rounded-xl flex items-center justify-center mb-4">
                <DollarSign className="w-7 h-7 text-caution" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Save Money</h3>
              <p className="text-gray-600 leading-relaxed">
                Average users save between $1,500 to $3,000 per year by reducing food waste.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100">
              <div className="w-14 h-14 bg-safe/20 rounded-xl flex items-center justify-center mb-4">
                <Leaf className="w-7 h-7 text-safe" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Reduce Waste</h3>
              <p className="text-gray-600 leading-relaxed">
                Help the planet by reducing your carbon footprint. Every item saved makes a difference.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-10 mb-16 border border-gray-100">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Powerful Features</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary-green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">📱 Smart Barcode Scanning</h4>
                  <p className="text-gray-600 text-sm">Scan products for instant nutrition facts & allergen warnings</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary-green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">📸 Photo Upload</h4>
                  <p className="text-gray-600 text-sm">Add photos to identify leftovers and partial items visually</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary-green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">🥗 Nutrition Tracking</h4>
                  <p className="text-gray-600 text-sm">See calories, protein, carbs & allergens for every item</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary-green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">👨‍🍳 Cook Before It Expires</h4>
                  <p className="text-gray-600 text-sm">Get meal suggestions from expiring ingredients</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary-green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">🏆 Achievement Badges</h4>
                  <p className="text-gray-600 text-sm">Earn badges for saving food and reducing waste</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary-green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">🌍 Community Impact</h4>
                  <p className="text-gray-600 text-sm">See global stats - items saved, CO₂ reduced, money saved</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary-green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">🏷️ Custom Categories</h4>
                  <p className="text-gray-600 text-sm">Create categories like "Kid's Snacks" or "Meal Prep"</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary-green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">👥 Household Sharing</h4>
                  <p className="text-gray-600 text-sm">Share your fridge with family members in real-time</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary-green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">🔔 Smart Reminders</h4>
                  <p className="text-gray-600 text-sm">Email notifications before food expires</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary-green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">📊 Analytics Dashboard</h4>
                  <p className="text-gray-600 text-sm">Track savings, waste reduction, and zero-waste streaks</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-10 mb-16 border border-gray-100">
            <div className="text-center mb-8">
              <div className="inline-block p-3 bg-primary-mint rounded-xl mb-4">
                <Smartphone className="w-8 h-8 text-primary-green" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">📱 Install Web App</h3>
              <p className="text-gray-600">Add FridgeLife to your home screen for quick access</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🍎</span>
                  <h4 className="font-bold text-gray-900">iPhone/iPad</h4>
                </div>
                <ol className="space-y-2 text-sm text-gray-700">
                  <li className="flex gap-2">
                    <span className="font-semibold text-primary-green">1.</span>
                    <span>Tap the <strong>Share</strong> button (square with arrow)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold text-primary-green">2.</span>
                    <span>Scroll and tap <strong>"Add to Home Screen"</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold text-primary-green">3.</span>
                    <span>Tap <strong>"Add"</strong> to confirm</span>
                  </li>
                </ol>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🤖</span>
                  <h4 className="font-bold text-gray-900">Android</h4>
                </div>
                <ol className="space-y-2 text-sm text-gray-700">
                  <li className="flex gap-2">
                    <span className="font-semibold text-primary-green">1.</span>
                    <span>Tap the <strong>Menu</strong> button (⋮)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold text-primary-green">2.</span>
                    <span>Tap <strong>"Add to Home Screen"</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold text-primary-green">3.</span>
                    <span>Tap <strong>"Add"</strong> to confirm</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-primary-green to-primary-green/80 rounded-2xl shadow-2xl p-12 text-center text-white">
            <h3 className="text-3xl font-bold mb-4">Ready to stop wasting food?</h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join thousands of people who are saving money and helping the planet.
            </p>
            <Link to="/signup" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-green font-semibold rounded-xl hover:bg-gray-50 shadow-xl transition-all hover:scale-105 whitespace-nowrap">
              Create Free Account
              <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="text-sm text-white/80 mt-4">{EMOJIS.SPARKLES} Free for 30 days {EMOJIS.BULLET} Takes 30 seconds</p>
          </div>
        </div>
      </div>

      <footer className="bg-white border-t border-gray-200 py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            <Link to="/terms" className="text-primary-green hover:text-primary-green/80 font-medium text-sm">
              Terms of Service
            </Link>
            <span className="text-gray-300">•</span>
            <Link to="/privacy" className="text-primary-green hover:text-primary-green/80 font-medium text-sm">
              Privacy Policy
            </Link>
          </div>
          <p className="text-gray-600 text-sm">Built with {EMOJIS.PLANT} by FridgeLife • Helping reduce food waste, one fridge at a time</p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/add-item" element={<AddItem />} />
          <Route path="/edit-item/:itemId" element={<EditItem />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/advanced-analytics" element={<AdvancedAnalytics />} />  {/* âœ… ADDED */}
          <Route path="/cook-before-expires" element={<CookBeforeExpires />} />
          <Route path="/household-settings" element={<HouseholdSettings />} />
		  <Route path="/accept-invite/:inviteId" element={<AcceptInvite />} />
		  <Route path="/join-household" element={<JoinHousehold />} />
		  <Route path="/pricing" element={<PricingPage />} />
		  <Route path="/terms" element={<TermsOfService />} />
		  <Route path="/privacy" element={<PrivacyPolicy />} />
		  <Route path="/how-to-use" element={<HowToUse />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
